import React, { useEffect, useMemo, useState } from "react";
import {
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import {
  CityBackground,
  CityPng,
  LightsLevels,
  LondonPng,
  MacaoPng,
  MonacoPng,
  ParisPng,
  ProfileChipPng,
  SofiaPng,
  TablesContainerPng,
  VegasPng,
  VipPng,
} from "../../../assets";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import { useTranslation } from "react-i18next";
import { useDevice } from "../../../utils/hooks/useDevice";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import BecomeVipModal from "../Modals/BecomeVipModal";
import i18n from "../../../i18n/i18n";
import baseLock from "../../../assets/main/vipButton/lock4_1.png";
import lockBg from "../../../assets/main/vipButton/lock4_2.png";
import lockFr from "../../../assets/main/vipButton/lock4_2_fr.png";
import lockEn from "../../../assets/main/vipButton/lock4_2_en.png";
import { UserProfile } from "../../../store/types";
import UnlockRoomModal from "../Modals/UnlockRoomModal";

const isRoomLocked = ({
  boughtRooms,
  room,
  playerLevel = 1,
}: {
  boughtRooms?: UserProfile["user_purchases"];
  room: Rooms;
  playerLevel?: number;
}) => {
  // Sofia, London, Paris, Macao, Monaco, Vegas
  let isRoomBought = false;
  if (boughtRooms) {
    switch (room.name.toLowerCase()) {
      case "sofia":
        isRoomBought = boughtRooms?.sofia || false;
        break;
      case "london":
        isRoomBought = boughtRooms?.london || false;
        break;
      case "paris":
        isRoomBought = boughtRooms?.paris || false;
        break;
      case "macao":
        isRoomBought = boughtRooms?.macao || false;
        break;
      case "monaco":
        isRoomBought = boughtRooms?.monaco || false;
        break;
      case "vegas":
        isRoomBought = boughtRooms?.vegas || false;
        break;
    }
  }

  // Room is locked if: player hasn't bought it AND player level is below minimum
  return !isRoomBought && playerLevel < room.minLevel;
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface IProps {
  index: number;
  room: Rooms;
  totalCount: number;
}

type Rooms = {
  id: string | number;
  name: string;
  minLevel: number;
  entryFee: number;
  isVip: Boolean;
  activeTablesCount: number;
};

const ROOM_NAME_TRANSLATION_KEYS: Record<string, string> = {
  vip: "T_SITE_VIP",
  sofia: "T_SITE_SOFIA",
  london: "T_SITE_LONDON",
  paris: "T_SITE_PARIS",
  monaco: "T_SITE_MONACO",
  macao: "T_SITE_MACAO",
  vegas: "T_SITE_VEGAS",
};

export const LobbyRoom = ({ room, index, totalCount }: IProps) => {
  const { t } = useTranslation();
  const roomDisplayName = ROOM_NAME_TRANSLATION_KEYS[
    room?.name?.trim().toLowerCase()
  ]
    ? t(ROOM_NAME_TRANSLATION_KEYS[room?.name?.trim().toLowerCase()])
    : room?.name;
  const { width } = useWindowDimensions();
  const { isTablet, isPortrait, isLandscape } = useDevice();
  const [isClient, setIsClient] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const isUserVip = auth?.user?.isVip || false;
  const isVipRoom = room?.isVip || room?.name?.toLowerCase() === "vip";
  const isLocked =
    isRoomLocked({
      boughtRooms: auth?.user?.user_purchases,
      room,
      playerLevel: auth?.user?.level,
    }) ||
    (isVipRoom && !isUserVip);
  const [showBecomeVipModal, setShowBecomeVipModal] = useState(false);
  const [showUnlockRoomModal, setShowUnlockRoomModal] = useState(false);

  const getLockImages = () => {
    const currentLang = i18n.language || "en";
    const lock4_1 = baseLock;

    let lock4_2;
    if (currentLang === "fr") {
      lock4_2 = lockFr;
    } else if (currentLang === "bg" || currentLang === "ru") {
      lock4_2 = lockBg;
    } else {
      lock4_2 = lockEn;
    }

    return { lock4_1, lock4_2 };
  };

  const { navigate } = useNavigation<Nav>();

  const isDesktop = width >= 980 && isLandscape;

  const isSpecial = React.useMemo(
    () => isLandscape && index % 3 === 1 && index + 2 < totalCount,
    [isLandscape, index, totalCount],
  );

  const desktopWidth = width / 3 - 50;
  const mobileWidth = width * 0.8;

  const roomImages: Record<string, any> = {
    paris: ParisPng,
    london: LondonPng,
    sofia: SofiaPng,
    macao: MacaoPng,
    monaco: MonacoPng,
    vegas: VegasPng,
    vip: VipPng,
  };

  const roomImage = roomImages[room?.name?.toLowerCase()] ?? CityPng;
  const isSmall = isLandscape && width < 980;

  const imageWidthPct = isLandscape
    ? isSpecial
      ? desktopWidth * 0.58 // desktop only
      : desktopWidth * 0.5
    : isTablet
      ? desktopWidth * 1.1 // tablet
      : mobileWidth * 0.6;

  const imageWidthPctBckd = isLandscape
    ? isSpecial
      ? desktopWidth * 0.78
      : desktopWidth * 0.7 // desktop only
    : isTablet
      ? desktopWidth * 1.4 // tablet
      : mobileWidth * 0.9;

  const handleRoomClick = () => {
    if (isLocked) {
      if (isVipRoom) {
        setShowBecomeVipModal(true);
      } else {
        setShowUnlockRoomModal(true);
      }
    } else {
      navigate("Room", {
        roomSlug: room?.name.toLowerCase(),
        roomId: String(room.id),
      });
    }
  };

  const SectionOne = useMemo(() => {
    return () => (
      <View style={styles.sectionOneWrap}>
        <Text
          style={[
            styles.roomTitle,
            {
              fontSize: isDesktop ? 30 : isSmall ? 22 : 28,
              marginTop: isDesktop ? -15 : 0,
            },
            isLocked && { opacity: 0.5 },
          ]}
        >
          {roomDisplayName}
        </Text>
        <ImageBackground
          source={TablesContainerPng}
          style={[styles.tableContainer, isLocked && { opacity: 0.5 }]}
          imageStyle={styles.tableContainerImage}
        >
          <TouchableOpacity onPress={handleRoomClick}>
            <Text
              style={[
                styles.tableText,
                { fontSize: isSmall ? 7 : 12, paddingHorizontal: 0 },
              ]}
            >
              {room?.activeTablesCount} {t("T_SITE_LOBBY_ROOM_TABLES")}
            </Text>
          </TouchableOpacity>
        </ImageBackground>
      </View>
    );
  }, [isDesktop, room, navigate, isLocked, handleRoomClick, roomDisplayName]);

  const SectionOneMobile = useMemo(() => {
    return () => (
      <View style={styles.sectionOneWrap}>
        <ImageBackground
          source={TablesContainerPng}
          style={[
            styles.tableContainer,
            isLocked && { opacity: 0.5 },
            {
              paddingHorizontal: isSmall ? 8 : 18,
              paddingVertical: isSmall ? 4 : 8,
            },
          ]}
          imageStyle={styles.tableContainerImage}
        >
          <TouchableOpacity onPress={handleRoomClick}>
            <Text
              style={[
                styles.tableText,
                { fontSize: isSmall ? 9 : 12, paddingHorizontal: 0 },
              ]}
            >
              {room?.activeTablesCount} {t("T_SITE_LOBBY_ROOM_TABLES")}
            </Text>
          </TouchableOpacity>
        </ImageBackground>

        <Text
          style={[
            styles.roomTitle,
            {
              fontSize: isDesktop ? 46 : isSmall ? 22 : 28,
              marginTop: isDesktop ? 4 : isSmall ? -8 : 0,
              marginBottom: isSmall ? 6 : 0,
            },
            isLocked && { opacity: 0.5 },
          ]}
        >
          {roomDisplayName}
        </Text>
      </View>
    );
  }, [isDesktop, room, navigate, isLocked, handleRoomClick, roomDisplayName]);

  const SectionTwo = () => {
    const lockImages = getLockImages();

    return (
      <View style={[styles.sectionTwoWrap, isDesktop && { flex: 1 }]}>
        <ImageBackground
          source={CityBackground}
          style={{
            width: imageWidthPctBckd,
            height: imageWidthPctBckd,
            justifyContent: "center",
            alignItems: "center",
            marginTop: -25,
            marginBottom: -25,
          }}
        >
          <TouchableOpacity onPress={handleRoomClick}>
            <View style={{ position: "relative" }}>
              <Image
                source={roomImage}
                style={{
                  width: imageWidthPct,
                  height: imageWidthPct,
                  resizeMode: "contain",
                  opacity: isLocked ? 0.4 : 1,
                }}
              />

              {isLocked && (
                <View style={styles.lockpadContainer}>
                  <Image
                    source={lockImages.lock4_1}
                    style={styles.lockBaseImage}
                    resizeMode="contain"
                  />

                  <Image
                    source={lockImages.lock4_2}
                    style={styles.lockTextImage}
                    resizeMode="contain"
                  />
                </View>
              )}
            </View>
          </TouchableOpacity>
        </ImageBackground>
      </View>
    );
  };

  const SectionThree = useMemo(() => {
    return () => (
      <View style={[styles.sectionThreeWrap, isLocked && { opacity: 0.5 }]}>
        <View
          style={[
            styles.stakeWrap,
            {
              paddingVertical: isDesktop ? 2 : isSmall ? 0 : 6,
              marginTop: isSmall ? 10 : 0,
            },
          ]}
        >
          <Text style={[styles.stakeLabel, { fontSize: isSmall ? 9 : 12 }]}>
            {t("T_Stake")}
          </Text>
          <ImageBackground
            source={ProfileChipPng}
            style={[
              styles.stakeChip,
              {
                width: isSmall ? 14 : 20,
                height: isSmall ? 14 : 20,
                marginTop: isSmall ? 0 : 2,
                marginRight: 3,
              },
            ]}
          />
          <Text style={[styles.stakeValue, { fontSize: isSmall ? 10 : 14 }]}>
            {room.entryFee}
          </Text>
        </View>
        <ImageBackground source={LightsLevels} style={styles.levelWrap}>
          <Text
            style={[
              styles.levelText,
              { fontSize: isDesktop ? 12 : isSmall ? 10 : 16 },
            ]}
          >
            {t("T_SITE_PROFILE_LEVEL")} {room?.minLevel}
          </Text>
        </ImageBackground>
      </View>
    );
  }, [isDesktop, room?.entryFee, isLocked]);

  const lockImages = getLockImages();

  return (
    <>
      <View style={[styles.wrapper, { width: "100%" }]}>
        <View
          style={{
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isDesktop ? (
            <>
              <SectionOne />
              <SectionTwo />
              <SectionThree />
            </>
          ) : (
            <>
              <SectionOneMobile />
              <SectionTwo />
              <SectionThree />
            </>
          )}
        </View>
      </View>

      {/* BecomeVipModal */}
      {showBecomeVipModal && (
        <BecomeVipModal onClose={() => setShowBecomeVipModal(false)} />
      )}
      {/* UnlockRoomModal */}
      {showUnlockRoomModal && (
        <UnlockRoomModal
          onClose={() => setShowUnlockRoomModal(false)}
          roomName={room.name}
          roomUnlockCost={1000} // todo This should ideally come from the room data
          minLevel={room.minLevel}
          lockImages={lockImages}
        />
      )}
    </>
  );
};

export default LobbyRoom;

const styles = StyleSheet.create({
  wrapper: {
    padding: 10,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "transparent",
    alignSelf: "center",
  },

  sectionOneWrap: {
    alignItems: "center",
  },
  tableContainer: {
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: "center",
  },
  tableContainerImage: {
    resizeMode: "stretch",
    borderRadius: 8,
  },
  tableText: {
    fontSize: 14,
    color: "#fff",
    paddingLeft: 20,
    paddingRight: 25,
    paddingVertical: 8,
    borderRadius: 6,
    fontWeight: "600",
  },
  roomTitle: {
    fontSize: 13,
    fontWeight: "700",
    letterSpacing: 0.6,
    color: "#E0E0E0",
    textShadowColor: "rgba(0, 0, 0, 0.6)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
    textTransform: "uppercase",
    textAlign: "center",
    paddingTop: 6,
  },

  sectionTwoWrap: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 2,
  },

  sectionThreeWrap: {
    alignItems: "center",
    gap: 6,
  },
  stakeWrap: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3A0909",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  stakeLabel: {
    color: "#FFffff",
    fontWeight: "bold",
    fontSize: 12,
    marginRight: 6,
  },
  stakeChip: {
    width: 24,
    height: 24,
    borderRadius: 8,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  stakeValue: {
    fontWeight: "bold",
    color: "#FFD700",
    fontSize: 14,
  },
  levelWrap: {
    paddingHorizontal: 20,
    paddingVertical: 6,
    borderRadius: 8,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
  },
  levelText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "bold",
    textShadowColor: "rgba(0, 0, 0, 0.7)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },

  desktopLeftCol: {
    flex: 1,
    justifyContent: "space-between",
    gap: 24,
    marginTop: "25%",
  },
  desktopRightCol: {
    marginTop: "15%",
  },

  lockpadContainer: {
    position: "absolute",
    top: 5,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },

  lockBaseImage: {
    position: "absolute",
    width: "50%",
    height: "50%",
    opacity: 0.8,
    top: "23.5%",
  },
  lockTextImage: {
    position: "absolute",
    width: "50%",
    height: "50%",
    opacity: 0.8,
  },
});
